// Only fields edited on Company details belong in its PATCH payload. In
// particular, do not resubmit hidden onboarding fields or uploaded file URLs.
export const organizationFieldLabels: Record<string, string> = {
  name: "Company Name",
  website: "Website",
  payment_url: "Pay Online URL",
  company_email: "Company Email",
  company_phone_number: "Company Phone",
  company_description: "Company Description",
  default_email_signature: "Default Email Signature",
  number_of_employees: "Number of Employees",
  years_in_business: "Years in Business",
  main_office_address_1: "Address Line 1",
  main_office_address_2: "Address Line 2",
  main_office_city: "City",
  main_office_state: "State",
  main_office_zip: "Zip Code",
  latitude: "Latitude",
  longitude: "Longitude",
  time_zone: "Time Zone",
  google_embedded_map: "Google Maps Embed Code",
  manufacturers: "Manufacturers & Brands",
  services: "Services Offered",
  facebook_url: "Facebook",
  instagram_url: "Instagram",
  twitter_url: "Twitter / X",
  linkedin_url: "LinkedIn",
  youtube_url: "YouTube",
  pinterest_url: "Pinterest",
  google_business_profile_url: "Google Business Profile",
  yelp_url: "Yelp",
  bbb_url: "BBB",
  tiktok_url: "TikTok",
  dnb_url: "Dun & Bradstreet",
  indeed_url: "Indeed",
  authorship_first_name: "Author First Name",
  authorship_last_name: "Author Last Name",
};
export const brandingFieldLabels: Record<string, string> = {
  overview: "Brand Overview",
  brand_color_1: "Primary Color",
  brand_color_2: "Secondary Color",
  brand_color_3: "Accent Color 1",
  brand_color_4: "Accent Color 2",
  brand_color_5: "Accent Color 3",
  brand_typeface_1: "Primary Typeface",
  brand_typeface_2: "Secondary Typeface",
};
export const requiredOrganizationFields = new Set([
  "name",
  "company_email",
  "company_phone_number",
  "main_office_address_1",
  "main_office_city",
  "main_office_state",
  "main_office_zip",
  "years_in_business",
]);
const normalized = (value: unknown) => (value == null ? "" : value);
export function changedFields<T extends object>(
  current: T,
  saved: Partial<T>,
  allowed: Record<string, string>,
): Partial<T> {
  return Object.fromEntries(
    Object.keys(allowed)
      .filter(
        (key) =>
          Object.prototype.hasOwnProperty.call(current, key) &&
          JSON.stringify(normalized(current[key as keyof T])) !==
            JSON.stringify(normalized(saved[key as keyof T])),
      )
      .map((key) => [key, current[key as keyof T] ?? null]),
  ) as Partial<T>;
}
export function requiredFieldErrors(values: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(values)
      .filter(
        ([key, value]) =>
          requiredOrganizationFields.has(key) &&
          (value == null || String(value).trim() === ""),
      )
      .map(([key]) => [
        key,
        `${organizationFieldLabels[key]} is required and cannot be cleared.`,
      ]),
  );
}
function messages(value: unknown): string {
  if (Array.isArray(value))
    return value.map(messages).filter(Boolean).join(" ");
  if (value && typeof value === "object")
    return Object.entries(value)
      .map(([key, item]) => `${key}: ${messages(item)}`)
      .join(" ");
  return typeof value === "string" ? value : "";
}
export function organizationSaveErrors(error: unknown): Record<string, string> {
  const data = (error as { responseData?: unknown })?.responseData;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const result = Object.fromEntries(
      Object.entries(data)
        .map(([key, value]) => [
          ["detail", "non_field_errors"].includes(key) ? "general" : key,
          messages(value),
        ])
        .filter(([, message]) => message),
    );
    if (Object.keys(result).length) return result;
  }
  return {
    general:
      "Your changes could not be saved. Check your connection and try again.",
  };
}
export function fieldLabel(key: string) {
  return (
    organizationFieldLabels[key] ||
    brandingFieldLabels[key] ||
    key.replaceAll("_", " ")
  );
}
